import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Progress } from "@/components/ui/progress";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import { Target, Plus, Edit, Trash2, UserCircle } from "lucide-react";
import { CurrencyDisplay } from "./CurrencySettings";

const PERIOD_TYPES = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-Yearly" },
  { value: "yearly", label: "Yearly (Fiscal Year)" }
];

const MONTHS = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
const QUARTERS = ["Q1 (Apr-Jun)", "Q2 (Jul-Sep)", "Q3 (Oct-Dec)", "Q4 (Jan-Mar)"];
const HALVES = ["H1 (Apr-Sep)", "H2 (Oct-Mar)"];

const RestaurantTargetManager = ({ restaurants, user }) => {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  // Fiscal year starts in April
  const currentFiscalYear = currentMonth >= 3 ? currentYear : currentYear - 1;

  // Check if user can edit (admin/superuser/manager all have full access)
  const canEdit = user?.role === 'admin' || user?.role === 'superuser' || user?.role === 'manager';

  const [targets, setTargets] = useState([]);
  const [targetsWithRevenue, setTargetsWithRevenue] = useState([]);
  const [storeManagers, setStoreManagers] = useState([]);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ isOpen: false, target: null });
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, targetId: null });
  const [assignManagerDialog, setAssignManagerDialog] = useState({ isOpen: false, target: null });
  const [loading, setLoading] = useState(false);
  const [loadingRevenue, setLoadingRevenue] = useState(false);
  const [selectedManagerId, setSelectedManagerId] = useState("");
  
  // Filters
  const [filterFiscalYear, setFilterFiscalYear] = useState(currentFiscalYear.toString());
  const [filterRestaurant, setFilterRestaurant] = useState("all");
  const [filterPeriodType, setFilterPeriodType] = useState("all");
  const [filterPeriodValue, setFilterPeriodValue] = useState("all");
  const [drilldownTarget, setDrilldownTarget] = useState(null);
  
  const [formData, setFormData] = useState({
    restaurant_id: "",
    fiscal_year: currentFiscalYear,
    period_type: "yearly",
    period_value: null,
    target_amount: "",
    store_manager_id: "",
  });

  const fetchTargets = async () => {
    try {
      const response = await axiosInstance.get("/restaurant-targets");
      setTargets(response.data);
    } catch (error) {
      toast.error("Failed to fetch targets");
      console.error(error);
    }
  };

  const fetchStoreManagers = async () => {
    try {
      const response = await axiosInstance.get("/store-managers");
      setStoreManagers(response.data);
    } catch (error) {
      console.error("Failed to fetch store managers:", error);
    }
  };

  const fetchRevenueData = useCallback(async () => {
    setLoadingRevenue(true);
    try {
      const targetsWithRevenueData = await Promise.all(
        targets.map(async (target) => {
          try {
            const params = new URLSearchParams({
              fiscal_year: target.fiscal_year.toString(),
              period_type: target.period_type,
            });
            
            if (target.period_value !== null && target.period_value !== undefined) {
              params.append("period_value", target.period_value.toString());
            }

            const response = await axiosInstance.get(
              `/restaurant-targets/calculate/${target.restaurant_id}?${params.toString()}`
            );
            
            return {
              ...target,
              actual_amount: response.data.actual_amount || 0,
              achievement_percentage: response.data.achievement_percentage || 0,
            };
          } catch (error) {
            console.error(`Failed to fetch revenue for target ${target.id}:`, error);
            return {
              ...target,
              actual_amount: 0,
              achievement_percentage: 0,
            };
          }
        })
      );
      
      setTargetsWithRevenue(targetsWithRevenueData);
    } catch (error) {
      console.error("Error fetching revenue data:", error);
    } finally {
      setLoadingRevenue(false);
    }
  }, [targets]);

  useEffect(() => {
    fetchTargets();
    fetchStoreManagers();
  }, []);

  useEffect(() => {
    if (targets.length > 0) {
      fetchRevenueData();
    }
  }, [targets, fetchRevenueData]);

  const getPeriodLabel = (target) => {
    if (target.period_type === "monthly") {
      return `${MONTHS[target.period_value - 1]} FY${target.fiscal_year}-${(target.fiscal_year + 1) % 100}`;
    } else if (target.period_type === "quarterly") {
      return `${QUARTERS[target.period_value - 1]} FY${target.fiscal_year}-${(target.fiscal_year + 1) % 100}`;
    } else if (target.period_type === "half_yearly") {
      return `${HALVES[target.period_value - 1]} FY${target.fiscal_year}-${(target.fiscal_year + 1) % 100}`;
    } else {
      return `FY ${target.fiscal_year}-${(target.fiscal_year + 1) % 100}`;
    }
  };

  const handleAdd = async () => {
    if (!formData.restaurant_id || !formData.target_amount) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        period_value: formData.period_type === "yearly" ? null : formData.period_value ? parseInt(formData.period_value) : null,
        store_manager_id: formData.store_manager_id || null
      };

      await axiosInstance.post("/restaurant-targets", payload);
      toast.success("Target created successfully!");
      setAddDialog(false);
      resetForm();
      fetchTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create target");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!editDialog.target) return;

    setLoading(true);
    try {
      const payload = {
        ...formData,
        target_amount: parseFloat(formData.target_amount),
        period_value: formData.period_type === "yearly" ? null : formData.period_value ? parseInt(formData.period_value) : null,
        store_manager_id: formData.store_manager_id || null
      };

      await axiosInstance.put(`/restaurant-targets/${editDialog.target.id}`, payload);
      toast.success("Target updated successfully!");
      setEditDialog({ isOpen: false, target: null });
      resetForm();
      fetchTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update target");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axiosInstance.delete(`/restaurant-targets/${deleteDialog.targetId}`);
      toast.success("Target deleted successfully!");
      setDeleteDialog({ isOpen: false, targetId: null });
      fetchTargets();
    } catch (error) {
      toast.error("Failed to delete target");
    }
  };

  const handleEdit = (target) => {
    setFormData({
      restaurant_id: target.restaurant_id,
      fiscal_year: target.fiscal_year,
      period_type: target.period_type,
      period_value: target.period_value,
      target_amount: target.target_amount.toString(),
      store_manager_id: target.store_manager_id || "",
    });
    setEditDialog({ isOpen: true, target });
  };

  const handleOpenAssignManager = (target) => {
    setSelectedManagerId(target.store_manager_id || "");
    setAssignManagerDialog({ isOpen: true, target });
  };

  const handleAssignManager = async () => {
    if (!assignManagerDialog.target) return;

    setLoading(true);
    try {
      const target = assignManagerDialog.target;
      const payload = {
        restaurant_id: target.restaurant_id,
        fiscal_year: target.fiscal_year,
        period_type: target.period_type,
        period_value: target.period_value,
        target_amount: target.target_amount,
        store_manager_id: selectedManagerId || null
      };

      await axiosInstance.put(`/restaurant-targets/${target.id}`, payload);
      toast.success("Store manager assigned successfully!");
      setAssignManagerDialog({ isOpen: false, target: null });
      setSelectedManagerId("");
      fetchTargets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign store manager");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      restaurant_id: "",
      fiscal_year: currentFiscalYear,
      period_type: "yearly",
      period_value: null,
      target_amount: "",
      store_manager_id: "",
    });
  };

  const generateFiscalYears = () => {
    const years = [];
    for (let i = currentFiscalYear - 2; i <= currentFiscalYear + 3; i++) {
      years.push(i);
    }
    return years;
  };

  const FISCAL_MONTHS_OPTIONS = [
    { value: "1", label: "April" }, { value: "2", label: "May" }, { value: "3", label: "June" },
    { value: "4", label: "July" }, { value: "5", label: "August" }, { value: "6", label: "September" },
    { value: "7", label: "October" }, { value: "8", label: "November" }, { value: "9", label: "December" },
    { value: "10", label: "January" }, { value: "11", label: "February" }, { value: "12", label: "March" },
  ];
  const QUARTER_OPTIONS = [
    { value: "1", label: "Q1 (Apr-Jun)" }, { value: "2", label: "Q2 (Jul-Sep)" },
    { value: "3", label: "Q3 (Oct-Dec)" }, { value: "4", label: "Q4 (Jan-Mar)" },
  ];
  const HALF_OPTIONS = [
    { value: "1", label: "H1 (Apr-Sep)" }, { value: "2", label: "H2 (Oct-Mar)" },
  ];

  const getPeriodValueOptions = () => {
    if (filterPeriodType === "monthly") return FISCAL_MONTHS_OPTIONS;
    if (filterPeriodType === "quarterly") return QUARTER_OPTIONS;
    if (filterPeriodType === "half_yearly") return HALF_OPTIONS;
    return [];
  };

  // Filter targets
  const displayTargets = (targetsWithRevenue.length > 0 ? targetsWithRevenue : targets).filter(t => {
    if (filterFiscalYear !== "all" && t.fiscal_year !== parseInt(filterFiscalYear)) return false;
    if (filterRestaurant !== "all" && t.restaurant_id !== filterRestaurant) return false;
    if (filterPeriodType !== "all" && t.period_type !== filterPeriodType) return false;
    if (filterPeriodValue !== "all" && t.period_value !== parseInt(filterPeriodValue)) return false;
    return true;
  });

  // Group by period type for display
  const groupedTargets = {
    yearly: displayTargets.filter(t => t.period_type === 'yearly'),
    half_yearly: displayTargets.filter(t => t.period_type === 'half_yearly'),
    quarterly: displayTargets.filter(t => t.period_type === 'quarterly'),
    monthly: displayTargets.filter(t => t.period_type === 'monthly'),
  };

  // Summary stats
  const summaryStats = {
    totalTarget: displayTargets.reduce((sum, t) => sum + (t.target_amount || 0), 0),
    totalActual: displayTargets.reduce((sum, t) => sum + (t.actual_amount || 0), 0),
    count: displayTargets.length,
    onTrack: displayTargets.filter(t => (t.achievement_percentage || 0) >= 80).length,
    behind: displayTargets.filter(t => (t.achievement_percentage || 0) < 80).length,
  };
  summaryStats.achievementPct = summaryStats.totalTarget > 0
    ? ((summaryStats.totalActual / summaryStats.totalTarget) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Restaurant Targets</h2>
          <p className="text-muted-foreground">
            {canEdit 
              ? "Set and manage revenue targets for restaurants (Fiscal Year: April-March)"
              : "View revenue targets for restaurants (Fiscal Year: April-March)"
            }
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setAddDialog(true)} className="gap-2">
            <Plus className="w-4 h-4" />
            Set New Target
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="shadow-lg border-0 bg-white/90">
        <CardContent className="pt-5 pb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Fiscal Year</Label>
              <Select value={filterFiscalYear} onValueChange={setFilterFiscalYear}>
                <SelectTrigger data-testid="target-filter-fy">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {generateFiscalYears().map((fy) => (
                    <SelectItem key={fy} value={fy.toString()}>FY {fy}-{(fy + 1) % 100 < 10 ? '0' : ''}{(fy + 1) % 100}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Restaurant</Label>
              <Select value={filterRestaurant} onValueChange={setFilterRestaurant}>
                <SelectTrigger data-testid="target-filter-restaurant">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Restaurants</SelectItem>
                  {restaurants && restaurants.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-gray-500">Period Type</Label>
              <Select value={filterPeriodType} onValueChange={(val) => { setFilterPeriodType(val); setFilterPeriodValue("all"); }}>
                <SelectTrigger data-testid="target-filter-period">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {PERIOD_TYPES.map((pt) => (
                    <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filterPeriodType !== "all" && filterPeriodType !== "yearly" && (
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">
                  {filterPeriodType === "monthly" ? "Month" : filterPeriodType === "quarterly" ? "Quarter" : "Half"}
                </Label>
                <Select value={filterPeriodValue} onValueChange={setFilterPeriodValue}>
                  <SelectTrigger data-testid="target-filter-period-value">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {getPeriodValueOptions().map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Quick Month & Quarter Select */}
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Month:</span>
              {MONTHS.map((m, idx) => {
                const monthVal = (idx + 1).toString();
                const currentFiscalMonth = currentMonth >= 3 ? currentMonth - 2 : currentMonth + 10;
                return (
                  <button
                    key={monthVal}
                    onClick={() => {
                      if (filterPeriodType === "monthly" && filterPeriodValue === monthVal) {
                        setFilterPeriodType("all");
                        setFilterPeriodValue("all");
                      } else {
                        setFilterPeriodType("monthly");
                        setFilterPeriodValue(monthVal);
                      }
                    }}
                    className={`px-2.5 py-1 text-xs font-medium rounded-full border transition-colors ${
                      filterPeriodType === "monthly" && filterPeriodValue === monthVal
                        ? "bg-blue-600 text-white border-blue-600"
                        : (idx + 1) === currentFiscalMonth
                          ? "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100"
                          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }`}
                    data-testid={`target-month-pill-${monthVal}`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-500 w-16">Quarter:</span>
              {QUARTERS.map((q, idx) => {
                const qVal = (idx + 1).toString();
                return (
                  <button
                    key={qVal}
                    onClick={() => {
                      if (filterPeriodType === "quarterly" && filterPeriodValue === qVal) {
                        setFilterPeriodType("all");
                        setFilterPeriodValue("all");
                      } else {
                        setFilterPeriodType("quarterly");
                        setFilterPeriodValue(qVal);
                      }
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                      filterPeriodType === "quarterly" && filterPeriodValue === qVal
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }`}
                    data-testid={`target-quarter-pill-${qVal}`}
                  >
                    {q}
                  </button>
                );
              })}
              {(filterPeriodType === "monthly" || filterPeriodType === "quarterly") && filterPeriodValue !== "all" && (
                <button
                  onClick={() => { setFilterPeriodType("all"); setFilterPeriodValue("all"); }}
                  className="px-3 py-1 text-xs font-medium rounded-full border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                  data-testid="target-clear-period-filter"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {displayTargets.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-blue-600 font-medium">Total Target</p>
              <p className="text-lg font-bold text-blue-700"><CurrencyDisplay amount={summaryStats.totalTarget} /></p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-green-600 font-medium">Total Actual</p>
              <p className="text-lg font-bold text-green-700"><CurrencyDisplay amount={summaryStats.totalActual} /></p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-purple-600 font-medium">Achievement</p>
              <p className="text-lg font-bold text-purple-700">{summaryStats.achievementPct}%</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-emerald-600 font-medium">On Track</p>
              <p className="text-lg font-bold text-emerald-700">{summaryStats.onTrack}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200">
            <CardContent className="p-3 text-center">
              <p className="text-xs text-red-600 font-medium">Behind</p>
              <p className="text-lg font-bold text-red-700">{summaryStats.behind}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Grouped Targets Display */}
      {displayTargets.length > 0 ? (
        <>
          {Object.entries(groupedTargets).map(([type, items]) => {
            if (items.length === 0) return null;
            const typeLabel = PERIOD_TYPES.find(p => p.value === type)?.label || type;
            return (
              <div key={type} className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">{typeLabel} Targets ({items.length})</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {items.map((target) => {
                    const achievementPercentage = target.achievement_percentage || 0;
                    const actualAmount = target.actual_amount || 0;
                    const isOnTrack = achievementPercentage >= 90;
                    const isWarning = achievementPercentage >= 50 && achievementPercentage < 90;
          
          return (
            <Card
              key={target.id}
              className="shadow-lg border-0 bg-white/90 hover:shadow-xl transition-all cursor-pointer"
              onClick={() => setDrilldownTarget(target)}
              data-testid={`target-card-${target.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isOnTrack ? 'bg-gradient-to-br from-green-500 to-emerald-500' :
                    isWarning ? 'bg-gradient-to-br from-yellow-500 to-orange-500' :
                    'bg-gradient-to-br from-red-500 to-pink-500'
                  }`}>
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{target.restaurant_name}</p>
                    <p className="text-xs text-gray-500">{getPeriodLabel(target)}</p>
                  </div>
                  <span className={`text-lg font-bold ${
                    isOnTrack ? 'text-green-600' : isWarning ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    {loadingRevenue ? "..." : `${achievementPercentage.toFixed(0)}%`}
                  </span>
                </div>
                <Progress 
                  value={Math.min(achievementPercentage, 100)} 
                  className="h-1.5"
                  indicatorClassName={
                    isOnTrack ? 'bg-green-500' : isWarning ? 'bg-yellow-500' : 'bg-red-500'
                  }
                />
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>Target: <CurrencyDisplay amount={target.target_amount} /></span>
                  <span>Actual: <CurrencyDisplay amount={actualAmount} /></span>
                </div>
              </CardContent>
            </Card>
          );
                  })}
                </div>
              </div>
            );
          })}
        </>
      ) : (
        <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-xl">
          <CardContent className="flex flex-col items-center justify-center p-12">
            <Target className="w-16 h-16 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Targets Found</h3>
            <p className="text-sm text-gray-500 mb-4">
              {canEdit 
                ? "No targets match the current filters. Try adjusting filters or set a new target."
                : "No revenue targets have been set yet"
              }
            </p>
            {canEdit && (
              <Button onClick={() => setAddDialog(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Set New Target
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Target Dialog */}
      <Dialog open={addDialog || editDialog.isOpen} onOpenChange={(open) => {
        if (!open) {
          setAddDialog(false);
          setEditDialog({ isOpen: false, target: null });
          resetForm();
        }
      }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editDialog.isOpen ? "Edit Target" : "Set New Target"}</DialogTitle>
            <DialogDescription>
              Set revenue target for a restaurant (Fiscal Year: April to March)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Restaurant *</Label>
              <Select
                value={formData.restaurant_id}
                onValueChange={(value) => setFormData({ ...formData, restaurant_id: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select restaurant" />
                </SelectTrigger>
                <SelectContent>
                  {restaurants && restaurants.map((restaurant) => (
                    <SelectItem key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Fiscal Year *</Label>
                <Select
                  value={formData.fiscal_year.toString()}
                  onValueChange={(value) => setFormData({ ...formData, fiscal_year: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {generateFiscalYears().map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        FY {year}-{(year + 1) % 100}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Period Type *</Label>
                <Select
                  value={formData.period_type}
                  onValueChange={(value) => setFormData({ ...formData, period_type: value, period_value: null })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PERIOD_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.period_type === "monthly" && (
              <div className="space-y-2">
                <Label>Select Month *</Label>
                <Select
                  value={formData.period_value?.toString()}
                  onValueChange={(value) => setFormData({ ...formData, period_value: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((month, index) => (
                      <SelectItem key={index} value={(index + 1).toString()}>
                        {month}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.period_type === "quarterly" && (
              <div className="space-y-2">
                <Label>Select Quarter *</Label>
                <Select
                  value={formData.period_value?.toString()}
                  onValueChange={(value) => setFormData({ ...formData, period_value: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select quarter" />
                  </SelectTrigger>
                  <SelectContent>
                    {QUARTERS.map((quarter, index) => (
                      <SelectItem key={index} value={(index + 1).toString()}>
                        {quarter}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.period_type === "half_yearly" && (
              <div className="space-y-2">
                <Label>Select Half *</Label>
                <Select
                  value={formData.period_value?.toString()}
                  onValueChange={(value) => setFormData({ ...formData, period_value: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select half" />
                  </SelectTrigger>
                  <SelectContent>
                    {HALVES.map((half, index) => (
                      <SelectItem key={index} value={(index + 1).toString()}>
                        {half}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Target Amount (₹) *</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Enter target amount"
                value={formData.target_amount}
                onChange={(e) => setFormData({ ...formData, target_amount: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Assign Store Manager</Label>
              <Select
                value={formData.store_manager_id}
                onValueChange={(value) => setFormData({ ...formData, store_manager_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="store-manager-select">
                  <SelectValue placeholder="Select store manager (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Store Manager Assigned</SelectItem>
                  {storeManagers
                    .filter(m => !formData.restaurant_id || m.restaurant_id === formData.restaurant_id)
                    .map((manager) => (
                      <SelectItem key={manager.id} value={manager.id}>
                        {manager.name} ({manager.restaurant_name})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {formData.restaurant_id && storeManagers.filter(m => m.restaurant_id === formData.restaurant_id).length === 0 && (
                <p className="text-xs text-amber-600">No store managers found for this restaurant. Add them in Settings → Store Managers.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setAddDialog(false);
              setEditDialog({ isOpen: false, target: null });
              resetForm();
            }}>
              Cancel
            </Button>
            <Button onClick={editDialog.isOpen ? handleUpdate : handleAdd} disabled={loading}>
              {loading ? "Saving..." : (editDialog.isOpen ? "Update Target" : "Set Target")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.isOpen} onOpenChange={(open) => setDeleteDialog({ isOpen: open, targetId: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Target</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this target? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Drilldown Dialog */}
      <Dialog open={!!drilldownTarget} onOpenChange={(open) => !open && setDrilldownTarget(null)}>
        <DialogContent className="max-w-lg" data-testid="target-drilldown-dialog">
          {drilldownTarget && (() => {
            const t = drilldownTarget;
            const achievementPct = t.achievement_percentage || 0;
            const actual = t.actual_amount || 0;
            const gap = t.target_amount - actual;
            const isOnTrack = achievementPct >= 90;
            const isWarning = achievementPct >= 50 && achievementPct < 90;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    {t.restaurant_name}
                  </DialogTitle>
                  <DialogDescription>{getPeriodLabel(t)}</DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Achievement Ring */}
                  <div className="flex items-center justify-center">
                    <div className={`w-28 h-28 rounded-full border-8 flex items-center justify-center ${
                      isOnTrack ? 'border-green-500 bg-green-50' :
                      isWarning ? 'border-yellow-500 bg-yellow-50' :
                      'border-red-500 bg-red-50'
                    }`}>
                      <div className="text-center">
                        <p className={`text-2xl font-bold ${isOnTrack ? 'text-green-600' : isWarning ? 'text-yellow-600' : 'text-red-600'}`}>
                          {achievementPct.toFixed(1)}%
                        </p>
                        <p className="text-[10px] text-gray-500">Achievement</p>
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2">
                    <div className="flex justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-sm text-blue-700">Target Amount</span>
                      <span className="font-bold text-blue-700"><CurrencyDisplay amount={t.target_amount} /></span>
                    </div>
                    <div className="flex justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-sm text-green-700">Actual Revenue</span>
                      <span className="font-bold text-green-700"><CurrencyDisplay amount={actual} /></span>
                    </div>
                    <div className={`flex justify-between p-3 rounded-lg ${gap > 0 ? 'bg-red-50' : 'bg-emerald-50'}`}>
                      <span className={`text-sm ${gap > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {gap > 0 ? 'Shortfall' : 'Surplus'}
                      </span>
                      <span className={`font-bold ${gap > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        <CurrencyDisplay amount={Math.abs(gap)} />
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="space-y-1">
                    <Progress 
                      value={Math.min(achievementPct, 100)} 
                      className="h-3"
                      indicatorClassName={isOnTrack ? 'bg-green-500' : isWarning ? 'bg-yellow-500' : 'bg-red-500'}
                    />
                  </div>

                  {/* Meta Info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {t.store_manager_name && (
                      <div className="flex items-center gap-2 text-gray-600 col-span-2">
                        <UserCircle className="w-4 h-4" />
                        <span>Store Manager: <strong>{t.store_manager_name}</strong></span>
                      </div>
                    )}
                    {t.notes && (
                      <div className="col-span-2 p-2 bg-gray-50 rounded text-gray-600 text-xs">
                        {t.notes}
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  {canEdit && (
                    <div className="flex gap-2 pt-2 border-t">
                      <Button
                        variant="outline"
                        className="flex-1 gap-2"
                        onClick={(e) => { e.stopPropagation(); setDrilldownTarget(null); handleEdit(t); }}
                      >
                        <Edit className="w-4 h-4" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 hover:text-red-700 gap-2"
                        onClick={(e) => { e.stopPropagation(); setDrilldownTarget(null); setDeleteDialog({ isOpen: true, targetId: t.id }); }}
                      >
                        <Trash2 className="w-4 h-4" /> Delete
                      </Button>
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RestaurantTargetManager;
